import { useSelector } from 'react-redux'

import {
  digObject, snakeToTitleCase, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

const defaultThemeColors = [
  { primary: '#8361BB', border: '#8361BB' },
  { primary: '#61ABBB', border: '#61ABBB' },
  { primary: '#99BB61', border: '#99BB61' },
  { primary: '#61BB95', border: '#61BB95' },
  { primary: '#6180BB', border: '#6180BB' },
  { primary: '#BBA761', border: '#BBA761' },
]

const generateTheme = (payload: { dataKeys: string[] }) => {
  const { dataKeys } = payload || {}

  const theme = dataKeys.reduce((acc, dataKey, index) => {
    // @ts-expect-error TS7053
    acc[dataKey] = {
      colors: defaultThemeColors[index] || defaultThemeColors[0],
      title: snakeToTitleCase(dataKey),
    }

    return acc
  }, {})

  return theme
}

const generateEndPoint = (chartKey: string, entityId?: number): string => {
  const endPoints: { [key: string]: string } = {
    brand_agencies_overview: `/charts/brands/${entityId}/agency_relations_overview.json`,
  }

  return endPoints[chartKey] || ''
}

const generateDataKey = (chartKey: string, options: { [key: string]: string | number } = {}): string => {
  const optionsString = Object.keys(options).reduce((acc, optionKey) => {
    const optionValue = options[optionKey]
    if (optionValue){
      return `${acc}-${optionKey}-${optionValue}`
    }

    return acc
  }, '')

  return `${chartKey}${optionsString}`
}

function useChartData(chartKey: string, options: { [key: string]: any } = {}) {
  const {
    chartDataKey: suppliedChartDataKey, dataKeyProps, entityId, requestOptions, sort = {},
  } = options

  const {
    sortDirection = 'asc',
    sortKey = 'title',
  } = sort || {}

  const excludedDataKeys = digObject(options, 'excludedDataKeys', [])

  const endpoint = generateEndPoint(chartKey, entityId)

  const chartDataKey = suppliedChartDataKey || generateDataKey(chartKey, requestOptions)

  // @ts-expect-error TS18046
  const entities = useSelector(reduxState => reduxState.entities)
  const { chartData } = entities

  const dataPayload = chartData[chartDataKey] || {}

  const dataArray = digObject(dataPayload, 'data', [])

  const filteredDataArray = sortArrayBy(dataArray, sortDirection, sortKey)

  const filteredDataArrayCount = filteredDataArray.length
  const hasfilteredDataArray = !!filteredDataArrayCount

  const loadMorePayload = useLoadMore({
    filters: sort,
    loadedCount: filteredDataArrayCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const chartOptions = {
    chart_data_key: chartDataKey,
    ...requestOptions,
    ...filtersWithOffset,
    limit,
    mergeData: true,
  }

  const { loading } = useReduxAction(
    'charts',
    'loadChartData',
    chartOptions,
    [chartDataKey, filtersWithOffset, performHttpRequests],
    {
      dispatchAction: action => action(endpoint, chartOptions),
      shouldPerformFn: () => !!endpoint && performHttpRequests,
    },
  )

  const firstDataObject = dataArray[0] || {}
  const dataKeys = Object.keys(firstDataObject).filter(k => !excludedDataKeys.includes(k))

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    chartDataKey,
    dataKeyProps,
    dataKeys,
    dataPayload,
    filteredDataArray,
    filteredDataArrayCount,
    hasfilteredDataArray,
    loading,
    theme: generateTheme({ dataKeys }),
  }
}

export default useChartData
